import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA_NUMBER = "919515562436";

const EXAMS = ["NATA", "JEE B.Arch", "UCEED", "CEED", "NID", "NIFT", "Not sure yet"];

export const EnquiryDialog = ({ open, onOpenChange, defaultProgram = "" }) => {
  const [form, setForm] = useState({ name: "", phone: "", exam: "NATA", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setForm((f) => ({ ...f, message: defaultProgram ? `I'm interested in the ${defaultProgram}.` : f.message }));
  }, [open, defaultProgram]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        phone: form.phone,
        exam: form.exam,
        program: defaultProgram || null,
        message: form.message || null,
      });
      const text = encodeURIComponent(
        `Hi Formline! I'd like to book a free demo class.\n\nName: ${form.name}\nPhone: ${form.phone}\nExam: ${form.exam}${
          defaultProgram ? `\nProgram: ${defaultProgram}` : ""
        }${form.message ? `\nNote: ${form.message}` : ""}`
      );
      toast.success("Enquiry sent — opening WhatsApp…");
      window.open(`https://wa.me/${WA_NUMBER}?text=${text}`, "_blank", "noopener");
      onOpenChange(false);
      setForm({ name: "", phone: "", exam: "NATA", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px] rounded-none border-2 border-brand p-0 gap-0" data-testid="enquiry-dialog">
        <div className="bg-brand text-white px-6 py-5">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold tracking-tight text-white">
              Book a free demo class
            </DialogTitle>
            <DialogDescription className="text-white/70 font-body">
              Tell us a little about you. We'll confirm your slot on WhatsApp.
            </DialogDescription>
          </DialogHeader>
        </div>
        <form onSubmit={submit} className="px-6 py-6 space-y-4 grid-paper-fine">
          <div className="space-y-1.5">
            <Label htmlFor="q-name" className="text-xs uppercase tracking-[0.18em] font-semibold text-brand">Full name</Label>
            <Input id="q-name" data-testid="enquiry-name" value={form.name} onChange={set("name")} placeholder="Your name" className="rounded-none border-ink/20 focus-visible:ring-brand" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="q-phone" className="text-xs uppercase tracking-[0.18em] font-semibold text-brand">Phone</Label>
            <Input id="q-phone" data-testid="enquiry-phone" value={form.phone} onChange={set("phone")} placeholder="+91 …" className="rounded-none border-ink/20 focus-visible:ring-brand" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="q-exam" className="text-xs uppercase tracking-[0.18em] font-semibold text-brand">Exam of interest</Label>
            <select
              id="q-exam"
              data-testid="enquiry-exam"
              value={form.exam}
              onChange={set("exam")}
              className="w-full h-10 px-3 border border-ink/20 rounded-none bg-white text-sm font-body focus:outline-none focus:ring-2 focus:ring-brand"
            >
              {EXAMS.map((x) => (
                <option key={x} value={x}>{x}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="q-msg" className="text-xs uppercase tracking-[0.18em] font-semibold text-brand">Message (optional)</Label>
            <Textarea id="q-msg" data-testid="enquiry-message" value={form.message} onChange={set("message")} rows={3} placeholder="Anything you'd like us to know?" className="rounded-none border-ink/20 focus-visible:ring-brand resize-none" />
          </div>
          <Button type="submit" data-testid="enquiry-submit" disabled={loading} className="w-full rounded-none bg-brand hover:bg-brand-dark text-white h-12 font-semibold tracking-wide group">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send &amp; open WhatsApp <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" /></>}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EnquiryDialog;
